import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PAYMENT_URL, PAYMENT_LABEL } from "@/lib/config";
import { Overline, EASE } from "./anim";

const POLICIES = {
  privacy: {
    title: "Privacy Policy",
    body: [
      "Kingdom Wealth Enterprises, LLC collects only the information you voluntarily provide through our forms: your name, email address, and the details you share about your goals.",
      "We use this information solely to respond to your inquiry, schedule consultations, and, with your consent, send occasional wealth-building insights and updates. We never sell, rent, or share your information with third parties for their marketing.",
      "Your data is stored securely and access is restricted. You may request correction or deletion of your information at any time by contacting us.",
    ],
  },
  terms: {
    title: "Terms of Service",
    body: [
      "This website is provided for general informational purposes. Nothing on this site constitutes investment, legal, tax, securities, or accounting advice, and use of this site does not create a client relationship.",
      "Consulting engagements are governed by a separate written agreement. Kingdom Wealth Enterprises, LLC does not guarantee any specific financial outcome or result.",
      "By submitting a form, you confirm the information you provide is accurate and consent to being contacted about your inquiry.",
    ],
  },
};

export default function Footer() {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "" });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [policy, setPolicy] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("Please agree to receive emails before subscribing.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/optins", form);
      setSubscribed(true);
      toast.success(data.message || "You are on the list.");
    } catch (err) {
      toast.error("Could not subscribe right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="border-t border-white/10 bg-[#060608]">
      <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <img
          src="/assets/logo.jpeg"
          alt="Kingdom Wealth Enterprises, LLC logo"
          data-testid="footer-logo"
          className="mb-12 h-24 w-auto"
        />
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-24">
          <div>
            <Overline>Stay Informed</Overline>
            <h2 className="mt-4 max-w-md text-2xl font-light leading-tight tracking-tight text-[#F9F8F6] sm:text-3xl">
              Wealth-building insights, occasionally in your inbox.
            </h2>
            <p className="mt-4 max-w-md text-sm font-light leading-relaxed text-[#A1A1AA]">
              Not ready to book? Join the list for a welcome note with practical insights, followed by occasional
              resources and opportunities. No noise, no pressure.
            </p>
          </div>

          <div>
            {subscribed ? (
              <div data-testid="optin-success" className="border border-[#C5A059]/30 bg-[#C5A059]/[0.05] p-8">
                <p className="text-lg font-light text-[#F9F8F6]">You are on the list.</p>
                <p className="mt-2 text-sm font-light text-[#A1A1AA]">
                  Watch your inbox for a welcome note from Kingdom Wealth Enterprises.
                </p>
              </div>
            ) : (
              <form data-testid="optin-form" onSubmit={submit} className="space-y-4" noValidate={false}>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="optin-first-name" className="kwe-label">First name</label>
                    <input
                      id="optin-first-name"
                      data-testid="optin-first-name-input"
                      className="kwe-input"
                      required
                      value={form.first_name}
                      onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                      placeholder="First name"
                      autoComplete="given-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="optin-last-name" className="kwe-label">Last name</label>
                    <input
                      id="optin-last-name"
                      data-testid="optin-last-name-input"
                      className="kwe-input"
                      required
                      value={form.last_name}
                      onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                      placeholder="Last name"
                      autoComplete="family-name"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="optin-email" className="kwe-label">Email address</label>
                  <input
                    id="optin-email"
                    data-testid="optin-email-input"
                    type="email"
                    className="kwe-input"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </div>
                <label className="flex cursor-pointer items-start gap-3 text-xs font-light leading-relaxed text-[#A1A1AA]">
                  <input
                    data-testid="optin-consent-checkbox"
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 appearance-none border border-white/20 bg-transparent transition-colors checked:border-[#C5A059] checked:bg-[#C5A059]"
                  />
                  I agree to receive emails from Kingdom Wealth Enterprises, LLC and understand I can unsubscribe
                  at any time.
                </label>
                <button data-testid="optin-submit-button" type="submit" disabled={submitting} className="btn-gold disabled:opacity-60">
                  {submitting ? "Subscribing..." : "Subscribe"}
                </button>
              </form>
            )}
          </div>
        </div>

        <div data-testid="compliance-disclaimer" className="mt-16 border-t border-white/10 pt-10">
          <p className="max-w-4xl text-xs font-light leading-relaxed text-[#71717A]">
            Kingdom Wealth Enterprises, LLC provides business and strategic consulting services only. Kingdom
            Wealth Enterprises, LLC is not a registered investment advisor, broker-dealer, certified financial
            planner, law firm, or CPA firm. Nothing on this website constitutes investment, legal, tax,
            securities, or accounting advice, and no content should be relied upon as such. Always consult
            appropriately licensed professionals before making financial, legal, or tax decisions.
          </p>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-white/10 pt-8 text-[11px] font-medium uppercase tracking-[0.22em] text-[#71717A]">
          <span>&copy; 2026 Kingdom Wealth Enterprises, LLC</span>
          {PAYMENT_URL && (
            <a
              data-testid="footer-pay-link"
              href={PAYMENT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#C5A059] transition-colors hover:text-[#D4AF37]"
            >
              {PAYMENT_LABEL}
            </a>
          )}
          <button data-testid="privacy-policy-link" onClick={() => setPolicy("privacy")} className="transition-colors hover:text-[#C5A059]">
            Privacy Policy
          </button>
          <button data-testid="terms-link" onClick={() => setPolicy("terms")} className="transition-colors hover:text-[#C5A059]">
            Terms of Service
          </button>
          <a data-testid="admin-link" href="/admin" className="ml-auto transition-colors hover:text-[#C5A059]">
            Admin
          </a>
        </div>
      </div>

      <AnimatePresence>
        {policy && (
          <motion.div
            data-testid="policy-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-5 backdrop-blur-sm"
            onClick={() => setPolicy(null)}
          >
            <motion.div
              initial={{ y: 32, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 32, opacity: 0 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="max-h-[80vh] w-full max-w-lg overflow-y-auto border border-white/10 bg-[#0F0F12] p-8"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={POLICIES[policy].title}
            >
              <div className="flex items-start justify-between gap-6">
                <h3 className="text-xl font-light tracking-tight text-[#F9F8F6]">{POLICIES[policy].title}</h3>
                <button data-testid="policy-close-button" onClick={() => setPolicy(null)} aria-label="Close" className="text-[#A1A1AA] transition-colors hover:text-[#C5A059]">
                  <X size={20} />
                </button>
              </div>
              <div className="mt-5 space-y-4">
                {POLICIES[policy].body.map((p) => (
                  <p key={p.slice(0, 24)} className="text-sm font-light leading-relaxed text-[#A1A1AA]">{p}</p>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </footer>
  );
}
